import { useMemo } from 'react';
import { useStoreActions, useStoreState } from '@discover-data/stores/restaurant/hooks';
import useCitySlug from '@discover-data/stores/hooks/useCitySlug';
import usePage from '@discover-data/stores/hooks/page/usePage';
import { type SuburbType } from '@discover-data/gql/fragments/suburb';
import { removeSessionSelectedRestaurantId } from '@discover-components/map/sessionSelectedRestaurantId';
import { removeSessionViewState } from '@discover-components/map/sessionViewState';
import isValidSuburb from './isValid';

export default function useSuburbs(
  allSuburbs: SuburbType[]
): [SuburbType[], (newSuburbs: SuburbType[]) => void] {
  const storeMode = useStoreState((state) => state.mode);
  const storeIds = useStoreState((state) => state.suburb.ids);
  const setStoreIds = useStoreActions((state) => state.suburb.set);
  const [, setCitySlug] = useCitySlug();
  const { resetPage } = usePage();

  const selectedSuburbs = useMemo(
    () =>
      storeIds
        .map((id) => allSuburbs.find((s) => s.id === id))
        .filter((s): s is SuburbType => !!s)
        .sort((a, b) => a.menuTitle?.localeCompare(b.menuTitle ?? '') ?? 0),
    [allSuburbs, storeIds]
  );

  async function set(newSuburbs: SuburbType[]) {
    const validSuburbs = newSuburbs.filter((s) => isValidSuburb(s, allSuburbs));

    resetPage();
    setStoreIds(validSuburbs.map(({ id }) => id));

    // reset the saved map position
    removeSessionSelectedRestaurantId();
    removeSessionViewState();

    await setCitySlug({ suburbs: validSuburbs });
  }

  return [selectedSuburbs, set];
}
